import {
  BodyText,
  Card,
  CardContent,
  CardHeader,
  HeaderText,
  Icons,
  Separator,
} from "@/components/ui";
import { formatDateTime } from "@/lib/time";
import type {
  TreatmentPlanPrescription,
  TreatmentPlanSummary,
} from "./useAmazonVisitHistory";

const getDispenseAmount = (prescription: TreatmentPlanPrescription) => {
  if (!prescription.dispenseQuantity) {
    return "Unknown";
  }

  const dispenseQuantityUnit = prescription.dispenseQuantityUnit ?? "Qty";
  return `${prescription.dispenseQuantity} ${dispenseQuantityUnit}`;
};
interface VisitSummaryProps {
  summary: TreatmentPlanSummary;
}

export const VisitSummary = ({ summary }: VisitSummaryProps) => {
  const conditionName = summary.condition ?? "Visit";
  const practitionerName = summary.providerName ?? "Unknown";
  const prescriptions = summary.prescriptions ?? [];

  return (
    <div className="flex gap-2 pt-4">
      <div className="flex flex-col items-center gap-2">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-info shadow-sm">
          <Icons.Hospital className="h-6 w-6 text-info" />
        </div>
        <Separator orientation="vertical" className="flex-grow" />
      </div>
      <div className="flex flex-col gap-2 flex-1">
        <div className="flex flex-col gap-1">
          <HeaderText tag="h3" size="md">
            {conditionName}
          </HeaderText>
          <BodyText size="sm" textColor="tertiary">
            Treatment plan sent {formatDateTime(summary.treatmentPlanDateTime)}
          </BodyText>
        </div>

        <div className="flex flex-col gap-3">
          {prescriptions.map((prescription, index) => {
            const key = `${prescription.medicationName}-${index}`;
            const dispenseAmount = getDispenseAmount(prescription);
            const daysSupply =
              typeof prescription.daysSupply === "number"
                ? prescription.daysSupply
                : "Unknown";
            const refills =
              typeof prescription.refills === "number"
                ? prescription.refills
                : "Unknown";
            const writtenDate = prescription.authoredOn
              ? formatDateTime(prescription.authoredOn)
              : "Unknown";

            return (
              <Card key={key} className="bg-secondary border-none p-3 pt-2">
                <CardHeader className="flex-row p-0 pb-1.5 gap-2 items-center">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-warning text-warning border border-warning/10">
                    <Icons.Pill className="h-5 w-5" />
                  </div>
                  <HeaderText tag="h4" size="sm">
                    {prescription.medicationName}
                  </HeaderText>
                </CardHeader>
                <Separator />
                <CardContent className="p-0 pt-2 flex flex-col gap-1">
                  <div className="flex gap-1">
                    <BodyText size="sm" bold>
                      Prescribed by:{" "}
                    </BodyText>
                    <BodyText size="sm" textColor="secondary">
                      {practitionerName}
                    </BodyText>
                  </div>
                  <div className="flex gap-1">
                    <BodyText size="sm" bold>
                      Date prescribed:{" "}
                    </BodyText>
                    <BodyText size="sm" textColor="secondary">
                      {writtenDate}
                    </BodyText>
                  </div>
                  <div className="flex gap-1">
                    <BodyText size="sm" bold>
                      Dispense amount:{" "}
                    </BodyText>
                    <BodyText size="sm" textColor="secondary">
                      {dispenseAmount}
                    </BodyText>
                  </div>
                  <div className="flex gap-1">
                    <BodyText size="sm" bold>
                      Days supply:{" "}
                    </BodyText>
                    <BodyText size="sm" textColor="secondary">
                      {daysSupply}
                    </BodyText>
                  </div>
                  <div className="flex gap-1">
                    <BodyText size="sm" bold>
                      Refills:{" "}
                    </BodyText>
                    <BodyText size="sm" textColor="secondary">
                      {refills}
                    </BodyText>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};
